import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ticketsApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Ticket, Clock, CheckCircle, WarningCircle, ArrowsClockwise,
  ChartBar, Users, Gauge, Plus, CaretRight,
} from '@phosphor-icons/react';
import { Toaster } from 'sonner';

const TicketDashboardPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try { const res = await ticketsApi.dashboard(); setData(res.data); }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" /></div>;
  if (!data) return null;

  const d = data;
  const maxCatCount = Math.max(...Object.values(d.category_counts || {}), 1);
  const maxPriCount = Math.max(...Object.values(d.priority_counts || {}), 1);

  const priorityColors = { critical: 'bg-red-500', high: 'bg-orange-500', medium: 'bg-amber-500', low: 'bg-slate-400' };
  const priorityLabels = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };

  return (
    <div className="space-y-5" data-testid="ticket-dashboard-page">
      <Toaster position="top-right" richColors />

      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900 via-slate-900 to-violet-900 rounded-2xl p-6 text-white" data-testid="ticket-dashboard-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-indigo-500/20 flex items-center justify-center border border-indigo-500/30">
              <Gauge className="h-6 w-6 text-indigo-400" weight="duotone" />
            </div>
            <div>
              <h1 className="text-xl font-bold font-['Outfit']" data-testid="dashboard-heading">Ticket Dashboard</h1>
              <p className="text-sm text-slate-300 mt-0.5">Overview of support tickets and issue resolution</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/20 gap-1.5 text-sm"
              onClick={() => navigate('/tickets')} data-testid="view-all-btn">
              <Ticket className="h-4 w-4" /> View All Tickets
            </Button>
            <Button variant="outline" className="border-violet-500/30 text-violet-300 hover:bg-violet-500/20 gap-1.5 text-sm"
              onClick={() => navigate('/tickets/config')} data-testid="config-btn">
              <Gauge className="h-4 w-4" /> Configuration
            </Button>
            <Button variant="outline" className="border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/20 gap-1.5 text-sm"
              onClick={() => navigate('/tickets/reports')} data-testid="reports-btn">
              <ChartBar className="h-4 w-4" /> Reports
            </Button>
            <Button className="bg-indigo-500 hover:bg-indigo-600 text-white gap-1.5 text-sm"
              onClick={() => navigate('/tickets/new')} data-testid="new-ticket-btn">
              <Plus className="h-4 w-4" /> New Ticket
            </Button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Total', value: d.total, icon: Ticket, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Open', value: d.open + d.assigned, icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'In Progress', value: d.in_progress, icon: ArrowsClockwise, color: 'text-violet-600', bg: 'bg-violet-50' },
          { label: 'Overdue', value: d.overdue, icon: WarningCircle, color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'Resolved', value: d.resolved + d.closed, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Escalated', value: d.escalated, icon: CaretRight, color: 'text-orange-600', bg: 'bg-orange-50' },
        ].map((s, i) => (
          <Card key={i} className={`${s.bg} border-0 shadow-sm`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{s.label}</p>
                  <p className={`text-2xl font-bold font-['Outfit'] mt-1 ${s.color}`}>{s.value}</p>
                </div>
                <s.icon className={`h-6 w-6 ${s.color}`} weight="duotone" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-[10px] text-slate-500 uppercase font-semibold">Avg Resolution Time</p>
            <p className="text-3xl font-bold font-['Outfit'] text-indigo-600 mt-1">{d.avg_resolution_hours || 0}<span className="text-base ml-1">hrs</span></p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-[10px] text-slate-500 uppercase font-semibold">SLA Compliance</p>
            <p className={`text-3xl font-bold font-['Outfit'] mt-1 ${d.sla_compliance_pct >= 90 ? 'text-emerald-600' : d.sla_compliance_pct >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
              {d.sla_compliance_pct}%
            </p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-[10px] text-slate-500 uppercase font-semibold">Reopened</p>
            <p className="text-3xl font-bold font-['Outfit'] text-rose-600 mt-1">{d.reopened}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Priority Breakdown */}
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="priority-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold font-['Outfit'] text-slate-800 flex items-center gap-2">
              <WarningCircle className="h-4 w-4 text-orange-500" /> Priority Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(d.priority_counts || {}).map(([k, v]) => (
              <div key={k} className="flex items-center gap-3">
                <span className="text-xs font-medium text-slate-600 w-16">{priorityLabels[k] || k}</span>
                <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${priorityColors[k] || 'bg-slate-400'} rounded-full transition-all flex items-center justify-end pr-2`}
                    style={{ width: `${Math.max((v / maxPriCount) * 100, 8)}%` }}>
                    <span className="text-[10px] font-bold text-white">{v}</span>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Category Breakdown */}
        <Card className="bg-white border border-slate-200 shadow-sm" data-testid="category-breakdown">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold font-['Outfit'] text-slate-800 flex items-center gap-2">
              <ChartBar className="h-4 w-4 text-indigo-500" /> Category Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(d.category_counts || {}).slice(0, 8).map(([k, v]) => (
              <div key={k} className="flex items-center gap-3">
                <span className="text-[10px] text-slate-600 w-32 truncate">{k}</span>
                <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-indigo-500 rounded-full transition-all flex items-center justify-end pr-2"
                    style={{ width: `${Math.max((v / maxCatCount) * 100, 8)}%` }}>
                    <span className="text-[9px] font-bold text-white">{v}</span>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* User Assignment */}
        {Object.keys(d.user_counts || {}).length > 0 && (
          <Card className="bg-white border border-slate-200 shadow-sm" data-testid="user-breakdown">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold font-['Outfit'] text-slate-800 flex items-center gap-2">
                <Users className="h-4 w-4 text-violet-500" /> User-wise Assignment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(d.user_counts || {}).map(([name, count]) => (
                <div key={name} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                  <span className="text-xs text-slate-700">{name}</span>
                  <Badge className="bg-violet-100 text-violet-700 border-0 text-[10px]">{count}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Branch Breakdown */}
        {Object.keys(d.branch_counts || {}).length > 0 && (
          <Card className="bg-white border border-slate-200 shadow-sm" data-testid="branch-breakdown">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold font-['Outfit'] text-slate-800">Branch-wise Tickets</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(d.branch_counts || {}).map(([name, count]) => (
                <div key={name} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                  <span className="text-xs text-slate-700">{name}</span>
                  <Badge className="bg-teal-100 text-teal-700 border-0 text-[10px]">{count}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TicketDashboardPage;
